<template>
  <div class="courses-page">
    <!-- Hero секція -->
    <CoursesHeroSection :category="category" />

    <!-- Контент курсов: показываем только после завершения начальной загрузки (локали + фильтр по языку интерфейса) -->
    <div class="courses-content">
      <!-- До завершения первой загрузки — скелетон в стиле списка курсов, чтобы не прыгал экран и не был виден футер -->
      <CoursesListSkeleton v-if="!isInitialLoadDone" :card-count="12" />

      <template v-else>
        <!-- Кнопка Назад перед списком курсов -->
        <div class="back-button-wrap">
          <UIButton variant="secondary-arrow"
                    arrow-direction="left"
                    :href="backHref"
                    :outline="false"
                    :text="t('courseCategory.courses.back')"
                    padding="18px 34px"
                    rounded="8px"
                    size="large"
                    @click="goBack" />
          <CourseLangSelector v-if="!hasError"
                              :available-locales="availableLocales"
                              v-model="selectedLocaleFilter" />
        </div>

        <!-- Error state -->
        <div v-if="hasError && !hasCourses"
             class="error-container">
          <UIInlineError :title="errorTitle"
                         :message="errorMessage"
                         :show-retry="true"
                         @retry="loadCourses" />
        </div>
        <!-- Courses list -->
        <CoursesList v-if="hasCourses"
                     :courses="courses"
                     :locale="currentLocale"
                     :category="category" />

        <!-- Pagination: показувати кнопку тільки коли є курси і є ще сторінки -->
        <div v-if="hasCourses && pagination?.hasNextPage"
             class="pagination">
          <UIButton variant="secondary-arrow"
                    arrow-direction="right"
                    :text="t('blog.showMore')"
                    :outline="true"
                    padding="18px 34px"
                    rounded="8px"
                    size="large"
                    :loading="loading"
                    @click="loadMoreCourses" />
        </div>

        <!-- Empty state -->
        <div v-else-if="!loading && !hasError && !hasCourses"
             class="empty">
          <p>{{ t('courseCategory.courses.empty') }}</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCourseLangSelector } from '@/composables/course'
import { useCourse } from '@/composables/course/useCourse'
import { useIndexRoutes } from '@/composables/locale/useIndexRoutes'
import { watch, computed, onMounted } from 'vue'
import { useSeo } from '@/composables/seo'

const { t } = useI18n()
definePageMeta({
  layout: 'full-width'
})

const route = useRoute()
const { currentLocale, buildPath, navigate, setupCategoryPageLoad } = useIndexRoutes()

// Категорія та локаль з параметрів URL (/:locale/courses/:category)
const category = computed(() => (route.params.category as string) ?? 'biblical')

const {
  courses,
  pagination,
  loading,
  error,
  hasCourses,
  hasError,
  availableLocales,
  fetchCourses,
  loadMore,
  fetchAllAvailableLocales,
  resetCategoryList,
} = useCourse()

const {
  selectedLocaleFilter,
  isInitialLoadDone,
  loadCourses,
  loadMoreCourses,
  loadCategoryData,
} = useCourseLangSelector({
  fetchCourses,
  fetchAllAvailableLocales,
  loadMore,
  availableLocales,
  category,
})

// SEO — реактивно при зміні категорії або локалі (як на index/article сторінках)
const { setupCourseSeo } = useSeo()
const fallbackSeoKey = 'biblical'
const categorySeoData = computed(() => {
  const cat = category.value
  const loc = currentLocale.value
  const title = t(`courseCategory.seo.${cat}.title`) || t(`courseCategory.seo.${fallbackSeoKey}.title`)
  const description = t(`courseCategory.seo.${cat}.description`) || t(`courseCategory.seo.${fallbackSeoKey}.description`)
  // Для canonical/alternate URL категорії: /locale/courses/category (без slug курсу)
  const slugForUrl = { [loc]: '' } as Record<string, string>
  return {
    title: { [loc]: title } as Record<string, string>,
    description: { [loc]: description } as Record<string, string>,
    slug: slugForUrl,
    categorySlug: cat,
    categoryTitle: title
  }
})

watch(
  () => [categorySeoData.value, currentLocale.value] as const,
  ([seoData]) => {
    if (seoData.title && Object.values(seoData.title)[0]) {
      setupCourseSeo({
        title: seoData.title,
        description: seoData.description,
        slug: seoData.slug,
        categorySlug: seoData.categorySlug,
        categoryTitle: seoData.categoryTitle
      })
    }
  },
  { immediate: true }
)

// Error handling
const errorTitle = computed(() => {
  if (!error.value) return ''
  return error.value.statusCode === 404
    ? t('courseCategory.errors.notFound.title')
    : t('courseCategory.errors.general.title')
})

const errorMessage = computed(() => {
  if (!error.value) return ''
  return error.value.statusCode === 404
    ? t('courseCategory.errors.notFound.message')
    : t('courseCategory.errors.general.message')
})

// Navigation (back to home with courses section hash)
const backHref = computed(() => buildPath({}) + '#courses-section')
const goBack = () => {
  navigate(buildPath({}) + '#courses-section')
}

const loadCategory = async () => {
  resetCategoryList()
  await loadCategoryData()
}
onMounted(loadCategory)
setupCategoryPageLoad(loadCategory)

// Watchers
watch(selectedLocaleFilter, () => {
  loadCourses()
}, { immediate: false })
</script>

<style scoped>
.courses-page {
  min-height: 100vh;
}

.courses-content {
  padding: 64px 40px 128px;
  max-width: var(--max-width-container);
  margin: 0 auto;
  width: 100%;
}

.back-button-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
}

@media (max-width: 1400px) {
  .courses-content {
    padding-left: 60px;
    padding-right: 60px;
  }
}

@media (max-width: 991px) {
  .courses-content {
    padding: 60px 30px 96px;
  }
}

@media (max-width: 768px) {
  .back-button-wrap {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .courses-content {
    padding-left: 20px;
    padding-right: 20px;
  }
}

@media (max-width: 767px) {
  .courses-content {
    padding: 40px 20px 64px;
  }
}

.empty {
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #666;
}

.error-container {
  display: flex;
  justify-content: center;
  padding: 2rem;
}

.pagination {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}
</style>
